import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Download, ExternalLink } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { STATUS_BADGE_CLASSES } from "@/pages/Invoices";

const PUBLIC_LANG_KEY = "invoicepro.public_lang";

function CopyableRow({ label, value, testIdBase }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div
          className="truncate font-mono text-sm text-slate-900"
          data-testid={`${testIdBase}-value`}
        >
          {value}
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          navigator.clipboard?.writeText(String(value));
          toast.success(t("publicInvoice.pay.copied", { label }));
        }}
        data-testid={`${testIdBase}-copy`}
        className="shrink-0 gap-1 text-xs"
      >
        <Copy className="h-3.5 w-3.5" />
        {t("common.copy") || "Copy"}
      </Button>
    </div>
  );
}

function PaymentModal({ open, onOpenChange, invoice, company, qrUrlPayment, qrUrlUqr, paymentQrMethod, uqrAvailable }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";
  const [uqrOk, setUqrOk] = useState(uqrAvailable !== false);
  const pi = company?.payment_info || {};
  const amount = invoice.amount_due > 0 ? invoice.amount_due : invoice.total;
  const paymentCaptionKey =
    paymentQrMethod === "swish"
      ? "publicInvoice.qr.captionSwish"
      : paymentQrMethod === "bankgiro" || paymentQrMethod === "plusgiro"
      ? "publicInvoice.qr.captionBgPg"
      : "publicInvoice.qr.captionUrlFallback";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="m:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="payment-modal"
      >
        <DialogHeader>
          <DialogTitle>{t("publicInvoice.pay.title")}</DialogTitle>
          <DialogDescription>
            {t("publicInvoice.pay.subtitle", {
              amount: formatCurrency(amount, invoice.currency, lang),
              due: formatDate(invoice.due_date, lang),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col items-center rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <img
                src={qrUrlPayment}
                alt="Payment QR"
                className="h-28 w-28 sm:h-40 sm:w-40 rounded bg-white p-1"
                data-testid="payment-modal-qr"
              />
              <div className="mt-3 text-center text-sm font-medium text-slate-700 leading-snug">
                {t(paymentCaptionKey)}
              </div>
            </div>
            {uqrOk && (
              <div className="flex flex-col items-center rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <img
                  src={qrUrlUqr}
                  alt="Invoice metadata QR"
                  className="h-28 w-28 sm:h-40 sm:w-40 rounded bg-white p-1"
                  onError={() => setUqrOk(false)}
                  data-testid="payment-modal-uqr"
                />
                <div className="mt-3 text-center text-sm font-medium text-slate-700 leading-snug">
                  {t("publicInvoice.pay.scanForData")}
                </div>
              </div>
            )}
          </div>
          <div className="grid gap-2">
            {pi.swish_number && (
              <CopyableRow
                label={t("publicInvoice.pay.swish")}
                value={pi.swish_number}
                testIdBase="payment-swish"
              />
            )}
            {pi.bankgiro && (
              <CopyableRow
                label={t("publicInvoice.pay.bankgiro")}
                value={pi.bankgiro}
                testIdBase="payment-bankgiro"
              />
            )}
            {pi.plusgiro && (
              <CopyableRow
                label={t("publicInvoice.pay.plusgiro")}
                value={pi.plusgiro}
                testIdBase="payment-plusgiro"
              />
            )}
            {pi.iban && (
              <CopyableRow
                label={t("publicInvoice.pay.iban")}
                value={pi.iban}
                testIdBase="payment-iban"
              />
            )}
            {pi.bic && (
              <CopyableRow
                label="BIC"
                value={pi.bic}
                testIdBase="payment-bic"
              />
            )}
            <CopyableRow
              label={t("publicInvoice.pay.ocr")}
              value={invoice.ocr_number}
              testIdBase="payment-ocr"
            />
            <CopyableRow
              label={t("publicInvoice.pay.amount")}
              value={`${amount.toFixed(2)} ${invoice.currency}`}
              testIdBase="payment-amount"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            data-testid="payment-modal-close"
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            {t("common.close") || "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PublicInvoice() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE}/public/invoices/${token}`)
      .then((res) => {
        if (!mounted) return;
        setData(res.data);
        const lang = res.data?.invoice?.pdf_language;
        const stored = localStorage.getItem(PUBLIC_LANG_KEY);
        if (lang && !stored) {
          i18n.changeLanguage(lang);
          localStorage.setItem(PUBLIC_LANG_KEY, lang);
        }
      })
      .catch((err) => {
        if (!mounted) return;
        const status = err?.response?.status;
        setError(status === 404 ? "not_found" : "error");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [token, i18n]);

  const language = i18n.language?.split("-")[0] || "en";

  const qrUrlPayment = useMemo(
    () => `${API_BASE}/public/invoices/${token}/qr?kind=payment`,
    [token]
  );
  const qrUrlUqr = useMemo(
    () => `${API_BASE}/public/invoices/${token}/qr?kind=uqr`,
    [token]
  );

  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-500"
        data-testid="public-loading"
      >
        {t("common.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="public-error">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="font-heading text-2xl tracking-tight text-slate-900">
            {t("publicInvoice.notFound.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {t("publicInvoice.notFound.body")}
          </p>
        </div>
      </div>
    );
  }

  const { company, invoice, pay_url, payment_methods_available } = data;
  const pdfUrl = `${API_BASE}/public/invoices/${token}/pdf?lang=${language}`;
  const stripeEnabled = !!pay_url;
  const localMethodsAvailable = payment_methods_available;
  const payClickable = stripeEnabled || localMethodsAvailable;

  function handlePayClick() {
    if (stripeEnabled) {
      // Stripe still stubbed — the /api/public/invoices/{token}/pay endpoint returns 503.
      axios
        .post(`${API_BASE}/public/invoices/${token}/pay`)
        .then(() => toast.message(t("publicInvoice.pay.stripeSoon")))
        .catch(() => toast.message(t("publicInvoice.pay.stripeSoon")));
      return;
    }
    if (localMethodsAvailable) {
      setPayOpen(true);
    }
  }

  const payTooltip = payClickable
    ? undefined
    : t("publicInvoice.pay.notConfigured");

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="public-invoice-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div>
            <div className="font-heading text-lg font-medium text-slate-900">
              {company.name}
            </div>
            {company.address && <div className="text-xs text-slate-500">{company.address}</div>}
          </div>
          <div className="flex items-center gap-3">
            <Badge
              className={STATUS_BADGE_CLASSES[invoice.status] || STATUS_BADGE_CLASSES.draft}
              data-testid="public-invoice-status"
            >
              {t(`invoices.status.${invoice.status}`)}
            </Badge>
            <span data-testid="public-lang-switcher">
              <LanguageSwitcher testId="public-lang-switcher-btn" />
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10 space-y-8">
        <section className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {invoice.type === "credit"
                ? t("publicInvoice.creditNote")
                : t("publicInvoice.invoice")}
            </div>
            <h1 className="font-mono mt-1 text-3xl tracking-tight text-slate-900">
              {invoice.invoice_number}
            </h1>
            <div className="mt-1 text-xs text-slate-500">
              {t("publicInvoice.ocrLabel")}:{" "}
              <span className="font-mono text-slate-900" data-testid="public-ocr">
                {invoice.ocr_number}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-end">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {t("publicInvoice.amountDue")}
              </div>
              <div
                className="font-heading text-3xl font-semibold tabular-nums text-slate-900"
                data-testid="public-amount-due"
              >
                {formatCurrency(
                  invoice.amount_due > 0 ? invoice.amount_due : invoice.total,
                  invoice.currency,
                  language
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" data-testid="public-download-pdf">
                <a href={pdfUrl} target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4 me-1" />
                  {t("publicInvoice.downloadPdf")}
                </a>
              </Button>
              <Button
                size="sm"
                onClick={handlePayClick}
                disabled={!payClickable}
                title={payTooltip}
                data-testid="public-pay-now"
                data-pay-mode={
                  stripeEnabled ? "stripe" : localMethodsAvailable ? "local" : "none"
                }
                className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {t("publicInvoice.payNow")}
                <ExternalLink className="h-4 w-4 ms-1 rtl:-scale-x-100" />
              </Button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3 rounded-xl border border-slate-200 bg-white p-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {t("publicInvoice.from")}
            </div>
            <div className="mt-1 font-medium text-slate-900">{company.name}</div>
            {company.address && <div className="text-sm text-slate-500">{company.address}</div>}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {t("publicInvoice.billTo")}
            </div>
            <div className="mt-1 font-medium text-slate-900">{invoice.customer.name}</div>
            {invoice.customer.address && (
              <div className="text-sm text-slate-500">
                {invoice.customer.address.street}
                <br />
                {invoice.customer.address.postal_code} {invoice.customer.address.city}
                <br />
                {invoice.customer.address.country}
              </div>
            )}
            {invoice.customer.email && (
              <div className="text-sm text-slate-500">{invoice.customer.email}</div>
            )}
          </div>
          <div className="text-end">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {t("publicInvoice.issued")}
            </div>
            <div className="text-sm">{formatDate(invoice.issue_date, language)}</div>
            <div className="mt-2 text-xs uppercase tracking-wide text-slate-500">
              {t("publicInvoice.delivered")}
            </div>
            <div className="text-sm">{formatDate(invoice.delivery_date, language)}</div>
            <div className="mt-2 text-xs uppercase tracking-wide text-slate-500">
              {t("publicInvoice.due")}
            </div>
            <div className="text-sm">{formatDate(invoice.due_date, language)}</div>
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-start text-xs uppercase tracking-wide text-slate-500">
                  {t("publicInvoice.artNr")}
                </th>
                <th className="px-4 py-3 text-start text-xs uppercase tracking-wide text-slate-500">
                  {t("publicInvoice.description")}
                </th>
                <th className="px-4 py-3 text-end text-xs uppercase tracking-wide text-slate-500">
                  {t("publicInvoice.qty")}
                </th>
                <th className="px-4 py-3 text-end text-xs uppercase tracking-wide text-slate-500">
                  {t("publicInvoice.unitPrice")}
                </th>
                <th className="px-4 py-3 text-end text-xs uppercase tracking-wide text-slate-500">
                  {t("publicInvoice.amount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.map((li, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{li.sku || ""}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{li.name}</div>
                    {li.description && <div className="text-xs text-slate-500">{li.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {li.quantity} {li.unit || ""}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {formatCurrency(li.unit_price, invoice.currency, language)}
                  </td>
                  <td className="px-4 py-3 text-end font-medium tabular-nums">
                    {formatCurrency(li.subtotal, invoice.currency, language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="ms-auto max-w-sm rounded-xl border border-slate-200 bg-white p-6 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">{t("publicInvoice.exVat")}</span>
              <span className="tabular-nums">
                {formatCurrency(invoice.subtotal, invoice.currency, language)}
              </span>
            </div>
            {invoice.discount_total > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500">{t("invoices.totals.discount")}</span>
                <span className="tabular-nums">
                  − {formatCurrency(invoice.discount_total, invoice.currency, language)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">{t("publicInvoice.vat")}</span>
              <span className="tabular-nums">
                {formatCurrency(invoice.tax_total, invoice.currency, language)}
              </span>
            </div>
            {Math.abs(invoice.rounding_adjustment || 0) > 0.001 && (
              <div className="flex justify-between">
                <span className="text-slate-500">{t("publicInvoice.rounding")}</span>
                <span className="tabular-nums">
                  {formatCurrency(invoice.rounding_adjustment, invoice.currency, language)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900">
              <span>{t("publicInvoice.toPay")}</span>
              <span className="tabular-nums" data-testid="public-total-topay">
                {formatCurrency(invoice.total, invoice.currency, language)}
              </span>
            </div>
            {invoice.amount_paid > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500">{t("invoices.totals.paid")}</span>
                <span className="tabular-nums text-emerald-700">
                  {formatCurrency(invoice.amount_paid, invoice.currency, language)}
                </span>
              </div>
            )}
            {invoice.amount_due > 0 && invoice.status !== "paid" && (
              <div className="flex justify-between font-medium text-red-700">
                <span>{t("invoices.totals.due")}</span>
                <span className="tabular-nums">
                  {formatCurrency(invoice.amount_due, invoice.currency, language)}
                </span>
              </div>
            )}
          </div>
        </section>

        {invoice.footer && (
          <footer className="text-center text-xs text-slate-500">{invoice.footer}</footer>
        )}
      </main>

      <PaymentModal
        open={payOpen}
        onOpenChange={setPayOpen}
        invoice={invoice}
        company={company}
        qrUrlPayment={qrUrlPayment}
        qrUrlUqr={qrUrlUqr}
        paymentQrMethod={data.payment_qr_method}
        uqrAvailable={data.uqr_available}
      />
    </div>
  );
}
